import React, { useEffect, useRef, useState } from 'react';
import { Dialog, Link, Loading, MessagePlugin, Space, Switch, Tooltip } from 'tdesign-react';
import { LazyLog } from '@melloware/react-logviewer';
import { useMemoizedFn } from 'ahooks';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { DownloadIcon, LoadingIcon } from 'tdesign-icons-react';
import { abortRunningLogRequest, getRunningLog } from '@/services/kopClusterService';
import { isVoid, sleep } from '@/utils/common';
import Descriptions from '@/components/Descriptions';
import {
  fetchInitialRunningLog,
  fetchRunningLog,
  resetLogState,
  resetFromNum,
} from '@/slices/kopClusterSlice';
import Editable from '@/components/Editable';

function Detail({
  detail,
  host,
  clusterName,
  fromNum,
  fetchInitialRunningLog,
  fetchRunningLog,
  resetLogState,
  loading,
  startLine,
  readNum,
  text,
  total,
  resetFromNum,
}) {
  const { logDispayName, logPath } = detail;

  const [visible, setVisible] = useState(false);
  const [canRefresh, setCanRefresh] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const ref = useRef();
  const intervalRef = useRef();

  const commonParams = {
    clusterName,
    host,
    logFileName: logPath,
  };

  const doInit = useMemoizedFn(async () => {
    await fetchInitialRunningLog({
      ...commonParams,
      startLine: 1,
      readNum: 0,
    });
    await sleep(10);
    loadData();
  });

  const getLines = (txt) => txt.split(/\r\n|\r|\n/).length;

  const loadData = useMemoizedFn(async (forceStartLine) => {
    // 强制执行
    const isForce = !isVoid(forceStartLine);

    let params = {
      ...commonParams,
      startLine,
      readNum,
    };

    // 如果强行刷新
    if (isForce) {
      resetFromNum(forceStartLine);
      await cleanInterval();
      params = {
        ...commonParams,
        startLine: forceStartLine,
        readNum: total - forceStartLine + 1,
      };
    }

    if (canRefresh || isForce) {
      await fetchRunningLog(params);
    }
    intervalRef.current = setTimeout(loadData, 3000);
  });

  // 重新获取
  const resetLogReader = async (forceStartLine) => {
    // 越界不读
    if (forceStartLine > total) {
      MessagePlugin.warning('超过日志最大行数，请重新输入！');
      return false;
    }
    await loadData(forceStartLine);
  };

  const cleanInterval = async () => {
    // 清空控件内容
    ref.current?.clear();

    // 立刻清除
    clearTimeout(intervalRef?.current);
    intervalRef.current = null;

    // 强行中止防逃逸
    abortRunningLogRequest();

    // 再次清除
    await sleep(10);
    clearTimeout(intervalRef?.current);
    intervalRef.current = null;
  };

  // 初始化以及开始
  useEffect(() => {
    if (visible) {
      doInit();
    } else {
      resetLogState();
      setCanRefresh(true);
    }
    return () => {
      cleanInterval();
    };
  }, [visible, doInit, resetLogState]);

  useEffect(() => {
    setTimeout(() => {
      ref.current?.handleScrollToLine(getLines(text) + 4);
    }, 100);
  }, [text]);

  const downloadFile = (filename, text) => {
    const element = document.createElement('a');
    element.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(text)}`);
    element.setAttribute('download', filename);

    element.style.display = 'none';
    document.body.appendChild(element);

    element.click();

    document.body.removeChild(element);
  };

  const doDownload = async () => {
    if (downloading) {
      return;
    }
    try {
      setDownloading(true);
      const {
        data: { logContent },
      } = await getRunningLog({
        ...commonParams,
        startLine: 1,
        readNum: total,
      });
      downloadFile(logDispayName, logContent);
    } catch (error) {
      console.trace(error);
      throw error;
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <Link theme='primary' onClick={() => setVisible(true)}>
        {logDispayName}
      </Link>
      <Dialog
        header={
          <Space size='small'>
            日志:{logDispayName}
            <Tooltip content='自动刷新开关：3s自动刷新一次' placement='left'>
              <Switch
                label={['开', '关']}
                value={canRefresh}
                onChange={() => setCanRefresh(!canRefresh)}
              />
            </Tooltip>
            <Loading style={{ marginLeft: 4 }} loading={loading} size='small' />
          </Space>
        }
        visible={visible}
        width='85%'
        placement='center'
        destroyOnClose
        onClose={() => setVisible(false)}
        footer={null}
      >
        <Descriptions column={4}>
          <Descriptions.Item label='日志总数' value={`${total}行`} />
          <Descriptions.Item
            label='起始行数'
            value={
              <>
                <Editable
                  text={fromNum}
                  type='number'
                  onChange={(newVal) => resetLogReader(newVal)}
                  inputProps={{ min: 1, decimalPlaces: 0, allowInputOverLimit: false }}
                />
              </>
            }
          />
          <Descriptions.Item label='当前显示' value={`最新${getLines(text) - 1}行`} />
          <Descriptions.Item
            label='操作'
            value={
              <Link
                theme='primary'
                prefixIcon={downloading ? <LoadingIcon /> : <DownloadIcon />}
                onClick={doDownload}
              >
                下载
              </Link>
            }
          />
        </Descriptions>
        <Root style={{ height: 600 }}>
          <LazyLog
            ref={ref}
            width='auto'
            height='auto'
            enableSearch
            caseInsensitive
            enableHotKeys
            text={text}
            selectableLines
          />
        </Root>
      </Dialog>
    </>
  );
}

export default connect(
  (state) => ({
    startLine: state.kopCluster.logDetail.startLine,
    readNum: state.kopCluster.logDetail.readNum,
    fromNum: state.kopCluster.logDetail.fromNum,
    total: state.kopCluster.logDetail.total,
    loading: state.kopCluster.logDetail.loading,
    text: state.kopCluster.logDetail.text,
  }),
  {
    fetchInitialRunningLog,
    fetchRunningLog,
    resetLogState,
    resetFromNum,
  },
)(Detail);

const Root = styled.div`
  margin-top: 10px;
  .react-lazylog {
    scrollbar-color: var(--td-scrollbar-color) transparent;
    scrollbar-width: thin;
    ::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }
    ::-webkit-scrollbar-thumb {
      border: 2px solid transparent;
      background-clip: content-box;
      background-color: white;
      border-radius: 15px;
    }
    ::-webkit-scrollbar-thumb:vertical:hover,
    ::-webkit-scrollbar-thumb:horizontal:hover {
      background-color: white;
    }
    ::-webkit-scrollbar-corner {
      background-color: #222;
    }
  }
`;
